import { Avatar } from 'components/Avatar';
import { useDate } from 'components/hooks';
import { slotTimes } from 'configs/active/constants';
import { ILocation, ISchedule, ISpeaker, ITeam, ITeamBooking } from 'configs/interfaces';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from 'rematch/store';

interface ITableCaptainBooking {
	item: ITeamBooking;
}

export const TableCaptainBooking = (props: ITableCaptainBooking) => {
	const { item } = props;
	const { getEventDateFromISOString } = useDate();

	const [team, setTeam] = useState<ITeam | undefined>(undefined);
	const [schedule, setSchedule] = useState<ISchedule | undefined>(undefined);
	const [speaker, setSpeaker] = useState<ISpeaker | undefined>(undefined);
	const [location, setLocation] = useState<ILocation | undefined>(undefined);

	const teams = useSelector((store: IRootState) => store.data.teams);

	useEffect(() => {
		let _team: typeof team = undefined;
		let _schedule: typeof schedule = undefined;
		let _speaker: typeof speaker = undefined;
		let _location: typeof location = undefined;

		if (teams) {
			_team = teams.find((e) => e.id === item.teamId);

			if (_team?.schedules) {
				_speaker = _team.schedules.find((e) => !e.isDeleted);
			}

			if (_team?.speakers) {
				_speaker = _team.speakers.find((e) => !e.isDeleted);
			}

			if (_team?.locations) {
				_location = _team.locations.find((e) => !e.isDeleted);
			}
		}

		setTeam(_team);
		setSchedule(_schedule);
		setSpeaker(_speaker);
		setLocation(_location);
	}, [teams, item]);

	const _renderSlotTime = () => {
		if (team) {
			if (schedule?.startDate && schedule?.endDate) {
				const start = getEventDateFromISOString(schedule.startDate);
				const end = getEventDateFromISOString(schedule.endDate);

				return (
					<div className="text-danger-500">{`${start.format('DD.MM.YYYY')} ${start.format('HH:mm')} - ${end.format(
						'HH:mm',
					)}`}</div>
				);
			}

			return <div style={{ height: '1.5rem' }} />;
		}

		if (slotTimes[item.slotId - 1]) {
			return <div className="text-danger-500">{slotTimes[item.slotId - 1]}</div>;
		}

		return <div style={{ height: '1.5rem' }} />;
	};

	const _renderImage = () => {
		if (team) {
			if (speaker?.image?.url) {
				return <Avatar size="xxl" avatar={speaker?.image?.url} fullName={team.title} />;
			}

			if (team.image?.url) {
				return <img src={team.image.url} className="w-20 xl:w-40 object-contain" alt="" />;
			}
		}

		return <div className="w-20 xl:w-40" />;
	};

	const _renderJobtitleAndCompany = (css: string, jobTitle?: string, company?: string) => {
		if (jobTitle || company) {
			let str = '';

			if (jobTitle && company) {
				str = `${jobTitle} | ${company}`;
			} else if (jobTitle) {
				str = jobTitle;
			} else if (company) {
				str = company;
			}

			return <div className={css}>{str}</div>;
		}

		return null;
	};

	const _renderLocation = () => {
		if (location) {
			let str = location.title;
			if (location.subtitle) {
				str = `${str} - ${location.subtitle}`;
			}
			return <div className="text-primary-500 font-semibold mt-4">{str}</div>;
		}

		return null;
	};

	const _renderUsers = () => {
		if (item.users.length > 0) {
			return item.users.map((user) => (
				<div key={`tablecaptain_${item.teamId}_users_${user.ticketcode}`} className="w-full flex items-center mb-2">
					<Avatar size="xs" fullName={`${user.firstName} ${user.lastName}`} />
					<div className="flex flex-col flex-1 pl-2">
						<div className="text-sm font-bold">{`${user.firstName} ${user.lastName}`}</div>
						{_renderJobtitleAndCompany('text-sm', user.jobTitle, user.company)}
					</div>
				</div>
			));
		}

		return <div className="flex-1 flex items-center justify-center font-bold text-primary-500">No bookings found yet</div>;
	};

	const _renderContent = () => {
		return (
			<div className="w-full flex flex-col" style={{ minHeight: 250 }}>
				<div className="w-full flex items-center mb-4">
					{_renderImage()}
					<div className="ml-4 flex flex-col text-left xl:mx-auto">
						<div className="text-primary-500 text-lg xl:text-2xl font-bold">{team?.title}</div>
						{_renderJobtitleAndCompany('xl:text-xl', speaker?.jobTitle, speaker?.company)}
						{_renderLocation()}
					</div>
				</div>
				<div className="font-bold text-danger-500 mb-2">Users</div>
				{_renderUsers()}
			</div>
		);
	};

	return (
		<div className="flex-1 flex p-8 mb-8">
			<div className="flex-1 flex flex-col border border-warning-500 rounded-lg">
				<div className="bg-warning-500 py-4 flex flex-col items-center justify-center">
					<div className="text-danger-500 font-bold text-2xl">{`Slot ${item.slotId}`}</div>
					{_renderSlotTime()}
				</div>
				<div className="flex-1 flex p-4">{_renderContent()}</div>
			</div>
		</div>
	);
};
